import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";
import SafeIcon from "./SafeIcon";
import type {
  ConverterTheme,
  SelectOption,
  SettingsPopoverProps,
} from "./types";

export const SettingsPopover = ({
  isOpen,
  onClose,
  triggerRef,
  title,
  subtitle,
  icon,
  theme,
  children,
  note,
}: SettingsPopoverProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      const insidePanel = panelRef.current?.contains(e.target);
      const insideTrigger = triggerRef.current?.contains(e.target);
      if (!insidePanel && !insideTrigger) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, triggerRef]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60]"
            onClick={onClose}
          />
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="absolute right-0 top-full z-[90] mt-2 w-[min(20rem,calc(100vw-2rem))] space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className={`${theme.settingsIconBox} rounded-xl p-2 text-white`}
                >
                  <SafeIcon icon={icon} className="text-sm" />
                </div>
                <div>
                  <h3 className="text-sm font-black leading-none text-slate-800 dark:text-white">
                    {title}
                  </h3>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {subtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close conversion settings"
                className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                type="button"
              >
                <SafeIcon icon={FiX} className="text-sm" />
              </button>
            </div>

            <div className="h-px bg-slate-100 dark:bg-slate-800" />
            <div className="space-y-6">{children}</div>
            {note && (
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                  {note}
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
  theme: ConverterTheme;
  hint?: string;
}

export const SliderField = ({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  theme,
  hint,
}: SliderFieldProps) => {
  const id = `setting-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const width = `${((value - min) / (max - min)) * 100}%`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="text-sm font-bold text-slate-700 dark:text-slate-200"
        >
          {label}
        </label>
        <span
          className={`min-w-[56px] text-right text-sm font-black tabular-nums ${theme.sliderText}`}
        >
          {value}
          {unit}
        </span>
      </div>
      <div className="relative flex h-5 items-center">
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700" />
        <div
          className={`absolute h-1.5 rounded-full bg-gradient-to-r ${theme.sliderTrack}`}
          style={{ width }}
        />
        <input
          id={id}
          name={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`relative z-10 h-5 w-full cursor-pointer appearance-none rounded-full bg-transparent focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 ${theme.sliderThumb} [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110`}
        />
      </div>
      {hint && (
        <p className="text-[11px] leading-tight text-slate-400">{hint}</p>
      )}
    </div>
  );
};

interface SelectFieldProps<T extends string> {
  label: string;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  theme: ConverterTheme;
  hint?: string;
}

export const SelectField = <T extends string>({
  label,
  value,
  options,
  onChange,
  theme,
  hint,
}: SelectFieldProps<T>) => {
  const id = `setting-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className="text-sm font-bold text-slate-700 dark:text-slate-200"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className={`w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none focus-visible:ring-2 ${theme.focusRing} dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint && (
        <p className="text-[11px] leading-tight text-slate-400">{hint}</p>
      )}
    </div>
  );
};

export default SettingsPopover;
